import { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter — smoothly animates a number from its previous value to
 * the new one. Uses requestAnimationFrame with an easeOutExpo curve so the
 * roll-up feels natural (fast at the start, settling gracefully).
 *
 * Designed as a drop-in replacement for `value.toLocaleString()` with the
 * same formatting options.
 *
 * Props:
 *   value           number  (required)  — the target value
 *   decimals        number              — fraction digits (default 2)
 *   minDecimals     number              — minimum fraction digits (default = decimals)
 *   duration        number              — animation duration in ms (default 600)
 *   prefix          string              — e.g. "$"
 *   suffix          string              — e.g. " FP"
 *   className       string
 *   ariaLabel       string
 */
export default function AnimatedCounter({
  value,
  decimals = 2,
  minDecimals = null,
  duration = 600,
  prefix = '',
  suffix = '',
  className = '',
  style,
  ariaLabel,
  'data-testid': dataTestId,
}) {
  const [display, setDisplay] = useState(Number(value) || 0);
  const fromRef = useRef(Number(value) || 0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Number(value) || 0;
    const start = fromRef.current;
    if (start === target) return;

    // If the change is tiny (<0.01) skip animation
    if (Math.abs(target - start) < 0.005) {
      fromRef.current = target;
      setDisplay(target);
      return;
    }

    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4); // easeOutQuart — clean settle

    const tick = (now) => {
      const elapsed = now - t0;
      const t = Math.min(1, elapsed / duration);
      const eased = ease(t);
      const next = start + (target - start) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, duration]);

  const fracMin = minDecimals !== null ? minDecimals : decimals;
  const formatted = display.toLocaleString(undefined, {
    minimumFractionDigits: fracMin,
    maximumFractionDigits: decimals,
  });

  return (
    <span className={`tabular-nums ${className}`} style={style} aria-label={ariaLabel} data-testid={dataTestId}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
